"use client"

import { useState, useEffect } from "react"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Badge } from "@/components/ui/badge"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { useToast } from "@/hooks/use-toast"
import { API_BASE_URL } from "@/lib/api"
import { useAuth } from "@/context/AuthContext"
import { Plus, Users, IndianRupee, Copy, Eye } from "lucide-react"
// Update your existing UserManagementDashboard component
// Add this import at the top
import { BookingManagement } from "@/components/admin/booking-management"


interface User {
  id: string
  userId: string
  name: string
  role: string
  balance: number
  referralCode: string
  referredBy?: string
  parent?: {
    userId: string
    name: string
    referralCode: string
    role: string
  }
  children: User[]
  _count: {
    children: number
  }
  createdAt: string
}

export function UserManagementDashboard() {
  const [users, setUsers] = useState<User[]>([])
  const [loading, setLoading] = useState(false)
  const [showCreateDialog, setShowCreateDialog] = useState(false)
  const [creating, setCreating] = useState(false)
  const { user: currentUser } = useAuth()
  const { toast } = useToast()

  const [newUser, setNewUser] = useState({
    userId: "",
    name: "",
    pin: "",
    role: "USER"
  })
// Add this state to the UserManagementDashboard component
const [selectedUser, setSelectedUser] = useState<User | null>(null)
const [showBookingsDialog, setShowBookingsDialog] = useState(false)

// Add this function to handle viewing user bookings
const viewUserBookings = (user: User) => {
  setSelectedUser(user)
  setShowBookingsDialog(true)
}


  const fetchUsers = async () => {
    setLoading(true)
    try {
      const token = localStorage.getItem('auth_token')
      const response = await fetch(`${API_BASE_URL}/admin/users`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      })

      if (response.ok) {
        const data = await response.json()
        setUsers(data.users || [])
      }
    } catch (error) {
      console.error('Error fetching users:', error)
      toast({
        title: "Error",
        description: "Failed to load users",
        variant: "destructive",
      })
    } finally {
      setLoading(false)
    }
  }

  const createUser = async () => {
    setCreating(true)
    try {
      const token = localStorage.getItem('auth_token')
      const response = await fetch(`${API_BASE_URL}/admin/users`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(newUser),
      })

      const data = await response.json()

      if (response.ok) {
        toast({
          title: "Success",
          description: "User created successfully",
          variant: "default",
        })
        setShowCreateDialog(false)
        setNewUser({ userId: "", name: "", pin: "", role: "USER" })
        fetchUsers()
      } else {
        toast({
          title: "Error",
          description: data.error || "Failed to create user",
          variant: "destructive",
        })
      }
    } catch (error) {
      console.error('Error creating user:', error)
      toast({
        title: "Error",
        description: "Failed to create user",
        variant: "destructive",
      })
    } finally {
      setCreating(false)
    }
  }

  const copyReferralCode = (code: string) => {
    navigator.clipboard.writeText(code)
    toast({
      title: "Copied!",
      description: "Referral code copied to clipboard",
    })
  }

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'ADMIN': return 'bg-red-500'
      case 'DISTRIBUTOR': return 'bg-blue-500'
      case 'AGENT': return 'bg-green-500'
      case 'USER': return 'bg-gray-500'
      default: return 'bg-gray-500'
    }
  }

  const getAvailableRoles = () => {
    if (!currentUser) return []
    
    switch (currentUser.role) {
      case 'ADMIN': return ['DISTRIBUTOR', 'AGENT', 'USER']
      case 'DISTRIBUTOR': return ['AGENT', 'USER']
      case 'AGENT': return ['USER']
      default: return []
    }
  }

  useEffect(() => {
    if (currentUser) {
      fetchUsers()
    }
  }, [currentUser])

  const myReferralCode = currentUser?.referralCode

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Users</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{users.length}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">My Referral Code</CardTitle>
            <Copy className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <div className="text-lg font-mono font-bold">{myReferralCode}</div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => copyReferralCode(myReferralCode || "")}
              >
                <Copy className="h-3 w-3" />
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">My Balance</CardTitle>
            <IndianRupee className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">₹{currentUser?.balance || 0}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">My Team</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {users.filter(u => u.referredBy === myReferralCode).length}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Create User Button */}
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">User Management</h2>
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogTrigger asChild>
            <Button className="bg-green-600 hover:bg-green-700">
              <Plus className="h-4 w-4 mr-2" />
              Create User
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create New User</DialogTitle>
              <DialogDescription>
                Create a new user in your team hierarchy
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label htmlFor="userId">User ID</Label>
                <Input
                  id="userId"
                  value={newUser.userId}
                  onChange={(e) => setNewUser({...newUser, userId: e.target.value})}
                  placeholder="Enter user ID"
                />
              </div>
              <div>
                <Label htmlFor="name">Name</Label>
                <Input
                  id="name"
                  value={newUser.name}
                  onChange={(e) => setNewUser({...newUser, name: e.target.value})}
                  placeholder="Enter full name"
                />
              </div>
              <div>
                <Label htmlFor="pin">PIN</Label>
                <Input
                  id="pin"
                  type="password"
                  value={newUser.pin}
                  onChange={(e) => setNewUser({...newUser, pin: e.target.value})}
                  placeholder="Enter 4-digit PIN"
                  maxLength={4}
                />
              </div>
              <div>
                <Label htmlFor="role">Role</Label>
                <Select
                  value={newUser.role}
                  onValueChange={(value) => setNewUser({...newUser, role: value})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select role" />
                  </SelectTrigger>
                  <SelectContent>
                    {getAvailableRoles().map(role => (
                      <SelectItem key={role} value={role}>
                        {role}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex justify-end space-x-2">
                <Button
                  variant="outline"
                  onClick={() => setShowCreateDialog(false)}
                >
                  Cancel
                </Button>
                <Button
                  onClick={createUser}
                  disabled={creating || !newUser.userId || !newUser.name || !newUser.pin}
                  className="bg-green-600 hover:bg-green-700"
                >
                  {creating ? "Creating..." : "Create User"}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Users Table */}
      <Card>
        <CardHeader>
          <CardTitle>User List</CardTitle>
          <CardDescription>
            Manage users in your hierarchy
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>User ID</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Referral Code</TableHead>
                <TableHead>Balance</TableHead>
                <TableHead>Team Size</TableHead>
                <TableHead>Parent</TableHead>
                <TableHead>Joined</TableHead>
                
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map((user) => (
                <TableRow key={user.id}>
                  <TableCell className="font-medium">{user.userId}</TableCell>
                  <TableCell>{user.name}</TableCell>
                  <TableCell>
                    <Badge className={getRoleColor(user.role)}>
                      {user.role}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <span className="font-mono">{user.referralCode}</span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => copyReferralCode(user.referralCode)}
                      >
                        <Copy className="h-3 w-3" />
                      </Button>
                    </div>
                  </TableCell>
                  <TableCell>₹{user.balance}</TableCell>
                  <TableCell>{user._count.children}</TableCell>
                  <TableCell>
                    {user.parent ? (
                      <div>
                        <div className="font-medium">{user.parent.userId}</div>
                        <div className="text-sm text-muted-foreground">
                          {user.parent.name} ({user.parent.role})
                        </div>
                      </div>
                    ) : (
                      <span className="text-muted-foreground">-</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {new Date(user.createdAt).toLocaleDateString()}
                  </TableCell>
{/* <TableCell>
  <Button
    variant="outline"
    size="sm"
    onClick={() => viewUserBookings(user)}
  >
    <Eye className="h-3 w-3 mr-1" />
    View Bookings
  </Button>
</TableCell>

// Add this dialog at the end of the component (before the closing div):
<Dialog open={showBookingsDialog} onOpenChange={setShowBookingsDialog}>
  <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
    <DialogHeader>
      <DialogTitle>
        Bookings for {selectedUser?.name} ({selectedUser?.userId})
      </DialogTitle>
    </DialogHeader>
    {selectedUser && (
      <BookingManagement userId={selectedUser.id} />
    )}
  </DialogContent>
</Dialog> */}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  )
}